/**
 * Portfolio Page (Web)
 * Consolidated portfolio overview - displays data from database
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this component only displays pre-calculated data.
 * All portfolio calculations are performed by backend Portfolio Service.
 */

import { useEffect, useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useConsolidatedPortfolio } from '@shared/features/portfolio';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { BrokerBreakdown } from './BrokerBreakdown';

export function PortfolioPage() {
  const {
    portfolio,
    brokerAccounts,
    loading,
    error,
    lastUpdated,
    refresh,
    triggerSync,
  } = useConsolidatedPortfolio({ includeHoldings: false, autoRefreshInterval: 60000 });

  const { accounts: allBrokerAccounts, loadAccounts } = useBrokerAccounts();
  
  // Track previous account IDs to detect new account connections
  const [previousAccountIds, setPreviousAccountIds] = useState<Set<string>>(new Set());
  
  // Ensure broker accounts are loaded
  useEffect(() => {
    if (allBrokerAccounts.length === 0) {
      loadAccounts();
    }
  }, [allBrokerAccounts.length, loadAccounts]);

  // Detect when new accounts are connected and refresh portfolio
  useEffect(() => {
    // Get current account IDs (use broker_account_id if available, otherwise id)
    const currentAccountIds = new Set(
      allBrokerAccounts.map(acc => acc.broker_account_id || acc.id)
    );
    
    // Check if any new accounts were added
    const hasNewAccounts = Array.from(currentAccountIds).some(
      id => !previousAccountIds.has(id)
    );
    
    // If new accounts detected and we had previous accounts, refresh portfolio
    if (hasNewAccounts && previousAccountIds.size > 0) {
      // New account connected - refresh portfolio data immediately
      // Add a small delay to allow backend to process the new account
      const timeoutId = setTimeout(() => {
        refresh();
      }, 2000); // Wait 2 seconds for backend to sync
      
      return () => clearTimeout(timeoutId);
    }
    
    // Update previous account IDs
    setPreviousAccountIds(currentAccountIds);
  }, [allBrokerAccounts, previousAccountIds, refresh]);
  
  // Also check if portfolio has all connected accounts
  // If portfolio is missing accounts that are connected, refresh
  // Use a ref to prevent infinite refresh loops
  const lastMissingAccountsCheckRef = useRef<number>(0);
  useEffect(() => {
    if (portfolio && allBrokerAccounts.length > 0 && brokerAccounts.length > 0 && !loading) {
      const portfolioAccountIds = new Set(
        brokerAccounts.map(acc => acc.broker_account_id)
      );
      const connectedAccountIds = new Set(
        allBrokerAccounts.map(acc => acc.broker_account_id || acc.id)
      );
      
      // Check if any connected account is missing from portfolio
      const missingAccounts = Array.from(connectedAccountIds).filter(
        id => !portfolioAccountIds.has(id)
      );
      
      // If accounts are missing, refresh (but only once per 10 seconds to prevent loops)
      const now = Date.now();
      if (missingAccounts.length > 0 && (now - lastMissingAccountsCheckRef.current > 10000)) {
        lastMissingAccountsCheckRef.current = now;
        // Some connected accounts are missing from portfolio - refresh
        const timeoutId = setTimeout(() => {
          refresh();
        }, 1000);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [portfolio, allBrokerAccounts, brokerAccounts, loading, refresh]);

  // Ensure portfolio data is fetched from DB on mount
  // This fetches all portfolio information that exists in the database
  useEffect(() => {
    // Always refresh on mount to ensure latest data is loaded
    // This is important when navigating to the page after account connection
    if (!loading) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);

  /** Re-fetch portfolio data from backend (no broker sync). */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  /** Open sync confirmation dialog. */
  const handleSyncClick = () => setSyncConfirmOpen(true);

  /** Close sync confirmation without syncing. */
  const handleSyncCancel = () => setSyncConfirmOpen(false);

  /** Sync from brokers (all connected accounts) then refresh display. */
  const handleSyncProceed = async () => {
    setSyncConfirmOpen(false);
    setIsSyncing(true);
    try {
      await triggerSync(undefined, 'both'); // no broker_account_id = sync all
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <Dialog open={syncConfirmOpen} onClose={handleSyncCancel}>
        <DialogTitle>Sync portfolio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Connected brokers will be synced. This may take a moment. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSyncCancel}>Cancel</Button>
          <Button onClick={handleSyncProceed} variant="contained" color="primary">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2.5, 
            mb: 3,
            background: 'linear-gradient(135deg, rgba(4, 55, 123, 0.05) 0%, rgba(4, 55, 123, 0.02) 100%)',
            border: '1px solid rgba(4, 55, 123, 0.1)',
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between">
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #04377b 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.02em',
              }}
            >
              Portfolio Overview
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title="Refresh portfolio data from database">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isRefreshing || isSyncing || loading}
                  color="primary"
                  size="small"
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'primary.main',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                  }}
                >
                  <RefreshIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sync portfolio data from all connected brokers">
                <IconButton
                  onClick={handleSyncClick}
                  disabled={isRefreshing || isSyncing || loading}
                  color="primary"
                  size="small"
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'primary.main',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                  }}
                >
                  <SyncIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </Paper>

        {(loading || isRefreshing || isSyncing) && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Show portfolio data from database */}
        {portfolio && (
          <>
            {/* Broker Account Breakdown */}
            {brokerAccounts.length > 0 ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  mb: 4, 
                  border: '1px solid rgba(4, 55, 123, 0.12)',
                  borderRadius: 3,
                  background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
                  boxShadow: '0 2px 8px rgba(4, 55, 123, 0.08)',
                }}
              >
                <BrokerBreakdown
                  brokerAccounts={brokerAccounts}
                  allBrokerAccounts={allBrokerAccounts}
                />
              </Paper>
            ) : (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  mb: 4, 
                  border: '1px solid rgba(4, 55, 123, 0.12)',
                  borderRadius: 3,
                  background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
                  boxShadow: '0 2px 8px rgba(4, 55, 123, 0.08)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {loading || isRefreshing || isSyncing
                    ? 'Loading portfolio data...' 
                    : 'No portfolio data found in database.'}
                </Typography>
              </Paper>
            )}

            {/* Last Updated */}
            {lastUpdated && (
              <Paper 
                sx={{ 
                  p: 2, 
                  mt: 4, 
                  bgcolor: 'rgba(4, 55, 123, 0.03)',
                  border: '1px solid rgba(4, 55, 123, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                >
                  Last updated: {new Date(lastUpdated).toLocaleString()}
                </Typography>
              </Paper>
            )}
          </>
        )}

        {!loading && !portfolio && !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Portfolio Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No portfolio data found in database.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleRefresh} disabled={isRefreshing || isSyncing}>
                Refresh
              </Button>
              <Button variant="contained" onClick={handleSyncClick} disabled={isRefreshing || isSyncing}>
                Sync from brokers
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </AuthenticatedLayout>
  );
}
